import { isString } from 'lodash'

import type { TimingSpan } from '~/data/api/client/assistant'

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60

/**
 * A duration as the minutes and seconds a label shows. Both are zero for a span under a second, which a label
 * words as "briefly".
 */
export type DurationParts = {
  minutes: number
  seconds: number
}

/**
 * The milliseconds a span took, or `undefined` while it is open or when either bound is not an ISO-8601 time
 * that leaves a non-negative span.
 */
export function spanDurationMs(span: Pick<TimingSpan, 'startedAt' | 'endedAt'> | null | undefined): number | undefined {
  if (!span?.endedAt) return undefined
  const duration = Date.parse(span.endedAt) - Date.parse(span.startedAt)
  return Number.isFinite(duration) && duration >= 0 ? duration : undefined
}

/**
 * The span the live stream puts under `providerMetadata.synop` on a finished reasoning part. The metadata is open
 * JSON, so the bounds are checked before use.
 */
export function readSynopSpan(providerMetadata: unknown): TimingSpan | undefined {
  if (typeof providerMetadata !== 'object' || providerMetadata === null || !('synop' in providerMetadata)) {
    return undefined
  }
  const { synop } = providerMetadata
  if (typeof synop !== 'object' || synop === null) return undefined
  const startedAt = 'startedAt' in synop ? synop.startedAt : undefined
  const endedAt = 'endedAt' in synop ? synop.endedAt : undefined
  if (!isString(startedAt) || !isString(endedAt)) return undefined
  return { startedAt, endedAt }
}

/** Splits `durationMs` into whole minutes and seconds, rounding to the nearest second once it reaches one. */
export function splitDuration(durationMs: number): DurationParts {
  if (durationMs < MS_PER_SECOND) return { minutes: 0, seconds: 0 }
  const totalSeconds = Math.round(durationMs / MS_PER_SECOND)
  return { minutes: Math.floor(totalSeconds / SECONDS_PER_MINUTE), seconds: totalSeconds % SECONDS_PER_MINUTE }
}
